import GG_DEFAULTS from '../../../../lib/genericGridDefaults';
import toString from 'lodash/toString';
import orderBy from 'lodash/orderBy';
import cloneDeep from 'lodash/cloneDeep';
import forEach from 'lodash/forEach';
import { GenericGridI, MarketplaceMatchesSettings, SurveyMatches, TermI } from '../../../../lib/types';

export const filterPurchasedAndSearch = (matches: SurveyMatches, showPurchased: boolean, filterText: string | undefined, data: any[], terms: TermI[], settings: MarketplaceMatchesSettings) => {
    const { purchased, unpurchased } = matches
    const surveyJobs = showPurchased ? purchased : unpurchased;

    if (!surveyJobs || surveyJobs?.length === 0) {
        return [];
    }

    let newData = cloneDeep(data);
    const termsArray = terms.map(e => e.term);
    const purchasedIndex = termsArray.indexOf('purchased');
    const industryIndex = termsArray.indexOf('surveyIndustry');
    const geographyIndex = termsArray.indexOf('surveyGeography');
    const employeeCountIndex = termsArray.indexOf('employeeCount');
    const companyCountIndex = termsArray.indexOf('companyCount');
    const confidenceIndex = termsArray.indexOf('confidence');

    // only filter on these terms when searching with input
    const validSearchIndexes = [
        termsArray.indexOf('surveyCode'),
        termsArray.indexOf('surveyCurrency'),
        termsArray.indexOf('surveyJobCode'),
        termsArray.indexOf('surveyTitle'),
        termsArray.indexOf('surveyJobTitle'),
        termsArray.indexOf('jobTitle')
    ];

    if (data) {
        const lower = toString(filterText).toLowerCase();

        newData = data.filter(row => {
            let match = false;
            for (let i = 0; i < row.length; i++) {
                if (validSearchIndexes.includes(i)) {
                    const thisLower = toString(row[i]).toLowerCase();

                    if (thisLower.includes(lower)) {
                        match = true;
                    }
                }
            }

            if (!match) {
                return false;
            }

            let shouldReturn = true;

            // filter on saved preferences from slideout
            if (settings) {
                const industriesList = settings.industries?.map(i => i.label?.toLowerCase() || '') || [];
                const geographiesList = settings.geographies?.map(i => i.label?.toLowerCase() || '') || [];

                if ((row[purchasedIndex] || false) !== showPurchased) {
                    shouldReturn = false;
                } else if (settings.minEmployee && row[employeeCountIndex] < settings.minEmployee) {
                    shouldReturn = false;
                } else if (settings.minCompany && row[companyCountIndex] < settings.minCompany) {
                    shouldReturn = false;
                } else if (industriesList.length > 0 && !industriesList.includes(row[industryIndex]?.toLowerCase())) {
                    shouldReturn = false;
                } else if (geographiesList.length > 0 && !geographiesList.includes(row[geographyIndex]?.toLowerCase())) {
                    shouldReturn = false;
                } else if (settings.onlyShowFrequent && row[confidenceIndex] < 0.3) {
                    shouldReturn = false;
                }
            }

            return shouldReturn;
        });
    }
    // Sort final result by confidence in descending order
    newData = orderBy(newData, [o => o[confidenceIndex]], ['desc']);

    return newData || [];
}

// combine grid data result and survey job recommendations
export const combineData = (matches: SurveyMatches, grid: GenericGridI, showPurchasedSurveys: boolean) => {
    const { purchased, unpurchased } = matches
    const newGrid = cloneDeep(grid);
    const surveyJobs = showPurchasedSurveys ? purchased : unpurchased;

    // If there are no results for selected tab, then skip combination logic below
    if (!surveyJobs || surveyJobs.length === 0 || !grid) {
        return newGrid;
    }

    let count = grid?.terms?.length;

    if (!showPurchasedSurveys) {
        newGrid.terms = [];
        count = 0;
    }

    // Create terms from recommender results and combine with grid terms
    surveyJobs?.forEach((surveyJob: any) => {
        forEach(surveyJob, (_, key) => {
            const check = newGrid?.terms?.filter((t: TermI) => t.term === key);
            if (check.length === 0) {
                newGrid?.terms.push({
                    id: count,
                    dataType: 'text',
                    term: key,
                    termType: 'field',
                    displayName: key,
                    name: key,
                    metadata: {
                        width: 100
                    }
                });
                count++;
            }
        });
    });

    const cutIdIndex = newGrid?.terms?.find(t => t.term === GG_DEFAULTS.CUT_ID)?.id;

    if (!showPurchasedSurveys) {
        newGrid.data = [];
        let index = -1;

        forEach(surveyJobs, surveyJob => {
            if (!surveyJob.purchased) {
                index++;
                newGrid.data[index] = [];
                forEach(surveyJob, value => {
                    newGrid.data[index].push(value);
                });
            }
        });
    } else {
        forEach(surveyJobs, surveyJob => {
            if (surveyJob.purchased) {
                forEach(newGrid.data, gridData => {
                    if (gridData[cutIdIndex as number] === surveyJob.cutId) {
                        forEach(surveyJob, value => {
                            gridData.push(value);
                        });
                    }
                });
            }
        });
    }

    return newGrid;
}

export const areSettingsEmpty = (settings: any) => {
    return (
        settings.geographies?.length === 0 &&
        settings.industries.length === 0 &&
        settings.minCompany == null &&
        settings.minEmployee == null &&
        settings.minMatchLikelihood === 30 &&
        settings.showNotPurchasedSurveys &&
        settings.showPurchasedSurveys
    );
}

export const checkForNoResultsAfterSearch = (isListFiltered: boolean, matches: any, grid: any, filteredData: any, searchText: string | undefined) => {
  const noResultsAfterSearching =
    !!(!matches?.loading && !grid?.loading && filteredData?.length === 0 && searchText && searchText !== '');

  const noResultsAfterFilter =
    !matches?.loading && !grid?.loading && filteredData?.length === 0 && isListFiltered;

  const noResultsWithoutFilters =
    !matches?.loading && !grid?.loading && filteredData?.length === 0 && !isListFiltered;

    return { noResultsAfterSearching, noResultsAfterFilter, noResultsWithoutFilters }
}

export const genNoDataText = (searchText: string | undefined, counts: any, showPurchasedSurveys: any) => {
  if (counts.purchased <= 0 && counts.unpurchased > 0 && showPurchasedSurveys) {
    return 'Your current pricing matches do not have any Marketplace Matches in your active library';
  }
  if (counts.purchased > 0 && counts.unpurchased <= 0 && !showPurchasedSurveys) {
    return 'Your current pricing matches do not have any suggested Marketplace Matches that are not already in your active library.';
  }
  if (searchText) return `No surveys found for your search entry "${searchText}"`;
  return 'No surveys found'
}